// The `runner` (box) wire shape — the DASHBOARD's view of a box, served by
// `GET /v1/runners` and `POST /v1/runners/:id/revoke`, and re-emitted on the
// `runners` topic of `/v1/streams`.
//
// This is the entity the resource layer was written for, and the measurement in
// `resource.ts`'s header is about it: the route's `RunnerRosterResource` published
// one key set, and the dashboard's hand-typed `RunnerRow` (`apps/dashboard/src/lib/
// fleet.ts`) declared a different one, with nothing that could fail between
// them. The projection is stated once here, and both sides now use it: the
// route serializes through these resources and the dashboard parses through
// these schemas, so the mirror is gone.
//
// ## Two variants, not one widened shape
//
// A revoke answers ONE box and the roster answers a page of them, and the
// roster row carries two extra blocks the single-box body has never had: the
// cross-org label (`owner_kind`/`owner_id`/`org_login`/`cross_org`) and the live
// presence fold (`stats`/`mode`). Widening the single body to carry them as
// nulls would change a shipped response, so the variants are two resources over
// one base schema — `RunnerRosterResource` extends `RunnerWireSchema` rather
// than restating it, so a field added to the base reaches both.
//
// ## `health` is an INPUT, and that is a boundary fact
//
// `boxHealth` — the five-way diagnosis `online | awaiting_first_beat |
// never_beat | stale | revoked` that `status` alone cannot express — lives in
// `packages/mcp` beside the `box` verb. `domain` may not import a package that
// imports it, so the resource takes the computed verdict rather than deriving
// it. That keeps ONE implementation of the diagnosis; a second one here, in
// the name of a self-contained serialize, is exactly the duplication this
// layer exists to remove.
//
// ## What is NOT here
//
// The MCP `box` verb's `maskBox` is a DIFFERENT body and stays its own: it is
// the operator door and carries `kind`, `owner_type`, `system_prompt`,
// `current_task_id`, `account_id`, a raw `address`, and `presence` as a nested
// object rather than two flattened fields. Merging the two would have to either
// drop fields from the operator view or leak them onto the tenant one. Two
// doors, two shapes, stated deliberately.

import { z } from 'zod';
import {
  BoxSpecsSchema,
  BoxStatsSchema,
  RunnerRoleSchema,
  RunnerStatusSchema,
} from '../entities/runner';
import { type SeatOrder, seatOrderNote } from '../value-objects/seat-order';
import { defineResource } from './resource';

/**
 * The five-way box diagnosis, as a published enum.
 *
 * Declared here rather than in `packages/mcp` so the wire shape names its own
 * vocabulary and the value can reach OpenAPI; `boxHealth`'s return type is
 * typed against it, so the producer and the published shape cannot disagree.
 */
export const RunnerHealthSchema = z.enum([
  /** A live presence key, or a beat inside the stale window. */
  'online',
  /** No beat yet, and still inside the first-beat grace window. */
  'awaiting_first_beat',
  /** No beat ever, and the grace window has passed. */
  'never_beat',
  /** Beat, but not recently enough. */
  'stale',
  /** Operator-killed credential. Terminal. */
  'revoked',
]);
export type RunnerHealth = z.infer<typeof RunnerHealthSchema>;

/**
 * The camelCase shape the projection reads plus the two derived values the
 * route computes for it. Structural on purpose — `Runner` (`entities/runner.ts`)
 * satisfies the `runner` half as it stands, which `runner.test.ts` proves by
 * serializing a parsed one.
 */
export type RunnerEntity = {
  runner: {
    id: string;
    status: z.infer<typeof RunnerStatusSchema>;
    displayName: string;
    hostname: string | null;
    specs: z.infer<typeof BoxSpecsSchema> | null;
    address: string | null;
    runnerVersion: string | null;
    lastHeartbeatAt: Date | null;
    enrolledAt: Date | null;
    revokedAt: Date | null;
    drainedAt: Date | null;
    cordonedAt: Date | null;
    createdAt: Date;
  };
  /** The diagnosis, computed by `boxHealth` (`@developerz/mcp`) — see the header. */
  health: { health: RunnerHealth; firstBeatExpectedBy?: Date };
};

/** A roster row: the base entity plus the two blocks only the roster carries. */
export type RunnerRosterEntity = RunnerEntity & {
  runner: RunnerEntity['runner'] & {
    ownerKind: 'org' | 'user';
    ownerId: string | null;
  };
  /**
   * Cross-org label — which org the box lives under, and whether it crossed the
   * line. Present only on the OWNER view (a user-bound session, #452); a PAT
   * gets the plain account roster and no label, so the four keys it produces
   * are absent rather than null.
   */
  org?: { orgLogin: string; crossOrg: boolean };
  /** Last-beat telemetry; `null` when the box is offline (no live presence key). */
  presence: {
    stats: z.infer<typeof BoxStatsSchema> | null;
    mode?: z.infer<typeof RunnerRoleSchema>;
  } | null;
  /**
   * The box's place in its account's seat order (2026-11-03) — present only for a box
   * the claim RANK-gates (an enrolled box of the roster's own account, among its live
   * ai-dev boxes); absent otherwise, so the key is absent rather than null.
   */
  seatOrder?: SeatOrder | undefined;
};

/** The single-box body — what `POST /v1/runners/:id/revoke` answers. */
export const RunnerWireSchema = z.object({
  id: z.string(),
  status: RunnerStatusSchema,
  /** Friendly box name — user-set or deterministic from the id, never a bare uuid. */
  display_name: z.string(),
  hostname: z.string().nullable(),
  /** Static hardware (cpu/cores/ram/disk/arch) the box advertised, or null. */
  specs: BoxSpecsSchema.nullable(),
  /** Public source IP observed at enroll — where the cockpit dials SSH (#115, #2004). */
  ssh_host: z.string().nullable(),
  runner_version: z.string().nullable(),
  last_heartbeat_iso: z.string().nullable(),
  enrolled_iso: z.string().nullable(),
  revoked_iso: z.string().nullable(),
  /**
   * When an operator drain HELD this box out (`runners.drained_at`), null when it is
   * not held. A drained box is `offline` by status and stays so, heartbeat or not,
   * until `POST /v1/runners/:id/undrain` — the one field telling it apart from a box
   * that merely stopped beating.
   *
   * `.nullish()` on READ, though the projection always emits it: this one schema
   * both serializes and is what the dashboard parses the roster with, and the
   * dashboard pod can roll before the api. A body from an api predating the mark
   * must parse — absent reads as not drained — or `/fleet` fails for that window.
   */
  drained_iso: z.string().nullish(),
  /**
   * When an operator CORDONED this box (`runners.cordoned_at`), null when not. A
   * cordoned box stays `online` and finishes its in-flight work, but is handed no new
   * task until undrained. `.nullish()` on read for the same roll-order reason as
   * `drained_iso`: absent reads as not cordoned.
   */
  cordoned_iso: z.string().nullish(),
  created_iso: z.string(),
  /**
   * The fine-grained diagnosis (#53), folded onto the same row `status`
   * collapses: `status` alone cannot tell "installing" from "crash-looped" from
   * "the VM is gone", because enrollment flips a row `pending → online` before
   * the box has ever dialed in.
   */
  health: RunnerHealthSchema,
  /** Only on `health: 'awaiting_first_beat'` — when the wait becomes `never_beat`. */
  first_beat_expected_by: z.string().optional(),
});

/** What a client should type against for a single-box body. */
export type RunnerWire = z.infer<typeof RunnerWireSchema>;

/**
 * The roster row — the base plus the cross-org label and the presence fold.
 *
 * The four cross-org keys are `.optional()` because they ride the OWNER view
 * only; `stats`/`mode` are `.nullable()` and always emitted, because the roster
 * always reads presence for its page. That split is the shipped contract, not a
 * convenience — a client reading the revoke response must not be told it
 * promises an `org_login`, and one reading a roster row must not have to tell
 * "offline" apart from "not looked up".
 */
export const RunnerRosterWireSchema = RunnerWireSchema.extend({
  /** Task-claim ownership (#452): `shared` is expressed as the absence of a pin. */
  owner_kind: z.enum(['org', 'user']).optional(),
  owner_id: z.string().nullable().optional(),
  /** Home org of the box (#452) — `accounts.github_login`. */
  org_login: z.string().optional(),
  /** True when the row lives outside the active account (always a caller-owned private box). */
  cross_org: z.boolean().optional(),
  /** Live box telemetry from the last presence beat; null when offline. */
  stats: BoxStatsSchema.nullable().optional(),
  /** The box's pinned role as of the last presence beat; null when offline. */
  mode: RunnerRoleSchema.nullable().optional(),
  /**
   * Where the box sits in its account's seat order (2026-11-03): its 1-based enrollment
   * `rank` among the live ai-dev boxes, the account's `seats` (null = unlimited), whether the
   * claim admits it (`within`), and the operator's sentence when it does not — `outside seat
   * count (14 of 12 seats)`: the box is enrolled and online, and will idle until a seat frees
   * or one is added. Absent for a box the seat rank does not gate.
   */
  seat_order: z
    .object({
      rank: z.number().int().positive(),
      seats: z.number().int().nonnegative().nullable(),
      within: z.boolean(),
      note: z.string().nullable(),
    })
    .optional(),
});

/** What a client should type against for a roster row. */
export type RunnerRosterWire = z.infer<typeof RunnerRosterWireSchema>;

/** The base projection, shared by both resources. */
function baseBody(e: RunnerEntity): RunnerWire {
  const r = e.runner;
  return {
    id: r.id,
    status: r.status,
    display_name: r.displayName,
    hostname: r.hostname,
    specs: r.specs,
    ssh_host: r.address,
    runner_version: r.runnerVersion,
    last_heartbeat_iso: r.lastHeartbeatAt ? r.lastHeartbeatAt.toISOString() : null,
    enrolled_iso: r.enrolledAt ? r.enrolledAt.toISOString() : null,
    revoked_iso: r.revokedAt ? r.revokedAt.toISOString() : null,
    drained_iso: r.drainedAt ? r.drainedAt.toISOString() : null,
    cordoned_iso: r.cordonedAt ? r.cordonedAt.toISOString() : null,
    created_iso: r.createdAt.toISOString(),
    health: e.health.health,
    ...(e.health.firstBeatExpectedBy !== undefined
      ? { first_beat_expected_by: e.health.firstBeatExpectedBy.toISOString() }
      : {}),
  };
}

export const RunnerResource = defineResource<RunnerEntity, typeof RunnerWireSchema>({
  schema: RunnerWireSchema,
  serialize: baseBody,
});

export const RunnerRosterResource = defineResource<
  RunnerRosterEntity,
  typeof RunnerRosterWireSchema
>({
  schema: RunnerRosterWireSchema,
  serialize: (e) => ({
    ...baseBody(e),
    ...(e.org !== undefined
      ? {
          owner_kind: e.runner.ownerKind,
          owner_id: e.runner.ownerId,
          org_login: e.org.orgLogin,
          cross_org: e.org.crossOrg,
        }
      : {}),
    // Always emitted on a roster row, as null when the box is offline: the
    // presence read runs for the whole page, so "we did not look" is not one of
    // the answers this row can carry.
    stats: e.presence?.stats ?? null,
    mode: e.presence?.mode ?? null,
    ...(e.seatOrder !== undefined && e.seatOrder.rank !== null
      ? {
          seat_order: {
            rank: e.seatOrder.rank,
            seats: Number.isFinite(e.seatOrder.seats) ? e.seatOrder.seats : null,
            within: e.seatOrder.within,
            note: seatOrderNote(e.seatOrder),
          },
        }
      : {}),
  }),
});

/**
 * `POST /v1/runners/:id/revoke` — the Runner body when the revoked row parses, else
 * exactly `{ id, status: 'revoked' }`. The second arm is the answer for a box whose row
 * no longer parses (the flip committed and was audited; a 500 once told an operator it
 * had failed) and for a retry of a revoke that already landed. Strict, and a subset of
 * the first arm — both keys mean what they mean on a Runner — so a client reading `id`
 * and `status` works on every answer.
 *
 * `z.xor`, not `z.union`: the arms are exclusive (the strict reduced arm cannot carry
 * the Runner's required fields), and only an exclusive union still reports EVERY arm
 * when nothing matches. Since zod 4.6 a plain union whose one arm failed without
 * aborting returns that arm's issues alone — here the reduced arm's "unrecognized
 * keys" — and drops the Runner arm's missing fields, which is the diagnosis a client
 * needs (`apps/dashboard/src/lib/fleet.test.ts`, "naming every field"). The OpenAPI
 * component reads `oneOf` accordingly.
 */
export const RunnerRevocationWireSchema = z.xor([
  RunnerWireSchema,
  z.object({ id: z.string(), status: z.literal('revoked') }).strict(),
]);

/** What a client should type the revoke answer against. */
export type RunnerRevocationBody = z.infer<typeof RunnerRevocationWireSchema>;

/** The revoke answer's two inputs: a parsed row, or only the id the flip matched. */
export type RunnerRevocationEntity =
  | { readonly kind: 'runner'; readonly entity: RunnerEntity }
  | { readonly kind: 'id_only'; readonly id: string };

export const RunnerRevocationResource = defineResource<
  RunnerRevocationEntity,
  typeof RunnerRevocationWireSchema
>({
  schema: RunnerRevocationWireSchema,
  serialize: (e) =>
    e.kind === 'runner' ? baseBody(e.entity) : { id: e.id, status: 'revoked' as const },
});
